/**
 * Contact Service
 * Handles all contact-related API calls including CSV upload
 */

import { API_BASE_URL, API_ENDPOINTS } from '../config/api';
import { 
  UploadContactsRequest, 
  UploadContactsResponse,
  GetContactsResponse,
  GetParticipantsResponse,
  NotificationMode 
} from '../types/contact';
import { getAuthToken } from '../utils/tokenManager';

/**
 * Upload contacts CSV file for a campaign
 * @param campaignId - The campaign ID
 * @param file - CSV file to upload
 * @param notificationModes - Array of notification modes (email, sms)
 * @param notificationText - Optional custom notification text with placeholders like {{name}}
 * @returns Upload response with uploaded count
 */
export const uploadContacts = async (
  campaignId: string,
  file: File,
  notificationModes: NotificationMode[],
  notificationText?: string
): Promise<UploadContactsResponse> => {
  const token = getAuthToken();
  
  if (!token) {
    throw new Error('Authentication token not found. Please log in again.');
  }

  // Create FormData for multipart/form-data request
  const formData = new FormData();
  formData.append('file', file);
  
  // Convert notification modes array to comma-separated string as per API spec
  // The API expects: notification_modes="email,sms"
  formData.append('notification_modes', notificationModes.join(','));
  
  // Add notification text if provided
  if (notificationText) {
    formData.append('notification_text', notificationText);
  }

  console.log('Uploading contacts:', {
    campaignId,
    fileName: file.name,
    fileSize: file.size,
    notificationModes,
    notificationText
  });

  const response = await fetch(
    `${API_BASE_URL.PROFILE}${API_ENDPOINTS.CONTACT.UPLOAD}/${campaignId}/upload`,
    {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        // Note: Do NOT set 'Content-Type' header for FormData
        // Browser will automatically set it with boundary
      },
      body: formData,
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    // Preserve the error structure for usage plan limit errors
    const error = new Error(
      errorData.message || 
      errorData.error?.message || 
      errorData.error || 
      `Contact upload failed with status ${response.status}`
    ) as any;
    // Attach the error code and full error object for handling
    if (errorData.error) {
      error.error = errorData.error;
      error.code = errorData.error.code;
    }
    throw error;
  }

  const result: UploadContactsResponse = await response.json();
  
  console.log('Contacts uploaded successfully:', result);
  return result;
};

/**
 * Get all contacts for a campaign
 * @param campaignId - The campaign ID
 * @returns List of contacts
 */
export const getContactsByCampaign = async (
  campaignId: string
): Promise<GetContactsResponse> => {
  const token = getAuthToken();
  
  if (!token) {
    throw new Error('Authentication token not found. Please log in again.');
  }

  const response = await fetch(
    `${API_BASE_URL.PROFILE}${API_ENDPOINTS.CONTACT.LIST}/${campaignId}`,
    {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.message || `Failed to fetch contacts with status ${response.status}`
    );
  }

  return await response.json();
};

/**
 * Helper function to upload contacts from form data
 * Validates and processes the upload request
 */
export const uploadContactsFromForm = async (
  request: UploadContactsRequest
): Promise<UploadContactsResponse> => {
  const { campaignId, file, notificationModes, notificationText } = request;

  // Validate campaign ID
  if (!campaignId) {
    throw new Error('Campaign ID is required to upload contacts');
  }

  // Validate file
  if (!file) {
    throw new Error('Please select a file to upload');
  }

  // Validate file type
  const allowedTypes = ['text/csv', 'application/vnd.ms-excel', 'application/csv'];
  const allowedExtensions = ['.csv', '.xlsx', '.xls'];
  const fileExtension = file.name.toLowerCase().slice(file.name.lastIndexOf('.'));
  
  if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(fileExtension)) {
    throw new Error('Invalid file type. Please upload a CSV or Excel file.');
  }

  // Validate file size (max 10MB)
  const maxSize = 10 * 1024 * 1024; // 10MB
  if (file.size > maxSize) {
    throw new Error('File size exceeds 10MB limit. Please upload a smaller file.');
  }

  // Validate notification modes
  if (!notificationModes || notificationModes.length === 0) {
    throw new Error('Please select at least one notification method (Email or SMS)');
  }

  return uploadContacts(campaignId, file, notificationModes, notificationText);
};

/**
 * Get campaign participants with pagination
 * Fetches the list of participants/contacts for a specific campaign
 * 
 * @param campaignId - The campaign ID
 * @param limit - Number of participants to fetch (default: 50)
 * @param offset - Offset for pagination (default: 0)
 * @returns Promise with participants list
 */
export const getCampaignParticipants = async (
  campaignId: string,
  limit: number = 50,
  offset: number = 0
): Promise<GetParticipantsResponse> => {
  const token = getAuthToken();
  
  if (!token) {
    throw new Error('Authentication token not found. Please log in again.');
  }

  const response = await fetch(
    `${API_BASE_URL.PROFILE}${API_ENDPOINTS.CONTACT.PARTICIPANTS}/${campaignId}/contacts?limit=${limit}&offset=${offset}`,
    {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.message || `Failed to fetch participants with status ${response.status}`
    );
  }

  const result: GetParticipantsResponse = await response.json();
  
  // Transform contacts to add computed status fields
  if (result.success && result.data?.contacts) {
    result.data.contacts = result.data.contacts.map(contact => ({
      ...contact,
      // Compute survey_status based on API fields
      survey_status: contact.survey_completed ? 'completed' : 
                     contact.survey_started ? 'pending' :
                     contact.notification_sent ? 'invited' : 'pending',
      // Compute review_status (placeholder logic - adjust based on your needs)
      review_status: 'not_started' as const,
      // Use created_at as joined_at
      joined_at: contact.created_at
    }));
  }
  
  console.log('Participants loaded:', result);
  return result;
};

